import React, { useMemo, useState } from 'react';
import { GitBranch, MousePointerClick, Plus } from 'lucide-react';
import { buildCanvasGridStyle, WORKFLOW_CONNECTOR_LINE } from '../../utils/workflowCanvasViewportUtils';
import {
  buildWorkflowProofCanvasSchema,
  buildStableMainLaneProofComparison,
  buildStableDrawerProofComparison,
} from '../../utils/workflowTriggerConnectorLayout';
import {
  buildSchemaBranchLanePaths,
  buildSchemaConditionBranchPaths,
  buildSchemaInsertPlusToNextPath,
  buildSchemaNodeLinkPath,
  buildRoundedOrthogonalTriggerMergePaths,
  buildConnectorJoinCover,
  getConditionBranchJunctionCovers,
  getTriggerMergeJunctionCovers,
} from '../../utils/workflowConnectorPaths';

import InteractiveAddActionProofHarness from './InteractiveAddActionProofHarness';

const STROKE = WORKFLOW_CONNECTOR_LINE.color;

function buildSchemaConnectorPaths(schema) {
  const paths = [];
  const junctionCovers = [];
  const firstDownstreamNode = schema.actionNode
    ?? schema.conditionNode
    ?? schema.mainEndNode;

  if (schema.topRowItemNodes.length > 1 && schema.triggerInsertPlusNode) {
    junctionCovers.push(...getTriggerMergeJunctionCovers({
      centerX: schema.visibleRowCenterX,
      mergeY: schema.mergeY,
      radius: schema.triggerCenterRadius,
    }));
  }

  paths.push(...buildRoundedOrthogonalTriggerMergePaths({
    topRowItemNodes: schema.topRowItemNodes,
    visibleRowCenterX: schema.visibleRowCenterX,
    mergeY: schema.mergeY,
    insertPlusNode: schema.triggerInsertPlusNode,
    radius: schema.triggerCornerRadius,
    centerRadius: schema.triggerCenterRadius,
  }));

  if (schema.triggerInsertPlusNode && firstDownstreamNode) {
    paths.push(buildSchemaInsertPlusToNextPath({
      insertPlusNode: schema.triggerInsertPlusNode,
      nextNode: firstDownstreamNode,
      laneCenterX: schema.visibleRowCenterX,
    }));
    junctionCovers.push(
      buildConnectorJoinCover({
        x: schema.visibleRowCenterX,
        y: schema.triggerInsertPlusNode.topY,
      }),
      buildConnectorJoinCover({
        x: schema.visibleRowCenterX,
        y: schema.triggerInsertPlusNode.bottomY,
      }),
    );
  }

  if (schema.actionNode && schema.mainPlusNode) {
    paths.push(buildSchemaNodeLinkPath({
      fromNode: schema.actionNode,
      toNode: schema.mainPlusNode,
    }));
  }

  if (schema.mainPlusNode && schema.conditionNode) {
    paths.push(buildSchemaNodeLinkPath({
      fromNode: schema.mainPlusNode,
      toNode: schema.conditionNode,
    }));
  } else if (schema.actionNode && schema.conditionNode) {
    paths.push(buildSchemaNodeLinkPath({
      fromNode: schema.actionNode,
      toNode: schema.conditionNode,
    }));
  }

  if (schema.conditionNode && schema.branchNodes.length > 0) {
    junctionCovers.push(...getConditionBranchJunctionCovers({
      parentCenterX: schema.conditionNode.centerX,
      railY: schema.branchRailY,
      centerRadius: schema.conditionCenterRadius,
    }));

    paths.push(...buildSchemaConditionBranchPaths({
      conditionNode: schema.conditionNode,
      branchNodes: schema.branchNodes,
      branchRailY: schema.branchRailY,
      radius: schema.branchCornerRadius,
      centerRadius: schema.conditionCenterRadius,
    }));

    schema.branchNodes.forEach((branchNode, index) => {
      const plusNode = schema.branchPlusNodes[index];
      paths.push(...buildSchemaBranchLanePaths({
        branchNode,
        plusNode,
        endNode: schema.branchEndNodes[index],
      }));
      junctionCovers.push(
        buildConnectorJoinCover({ x: branchNode.centerX, y: plusNode.topY }),
        buildConnectorJoinCover({ x: branchNode.centerX, y: plusNode.bottomY }),
      );
    });
  }

  return { paths, junctionCovers };
}

function ProofTriggerCard({ node }) {
  return (
    <div
      className="absolute flex w-[340px] flex-col overflow-hidden rounded-xl border border-zinc-700 bg-zinc-900 shadow-lg"
      style={{ left: node.x, top: node.y, width: node.width, height: node.height }}
      data-workflow-proof-node={node.id}
      data-workflow-proof-node-type={node.type}
    >
      <div className="flex items-center gap-2 border-b border-zinc-800 px-4 py-3">
        <MousePointerClick size={16} className="text-emerald-400" />
        <span className="text-sm font-semibold text-white">{node.label}</span>
      </div>
      <div className="px-4 py-3 text-xs text-zinc-400">No filters applied</div>
      <div className="mt-auto flex items-center justify-end gap-2 border-t border-zinc-800 px-3 py-2 text-xs text-zinc-500">
        <span>Stats</span>
      </div>
    </div>
  );
}

function ProofAddTriggerCard({ node }) {
  return (
    <div
      className="absolute flex h-[132px] w-[340px] flex-col items-center justify-center rounded-xl border-2 border-dashed border-emerald-500/60 bg-zinc-900/50"
      style={{ left: node.x, top: node.y, width: node.width, height: node.height }}
      data-workflow-proof-node={node.id}
      data-workflow-proof-node-type={node.type}
    >
      <Plus size={20} className="mb-2 text-emerald-400" />
      <span className="text-sm font-medium text-emerald-400">{node.label}</span>
    </div>
  );
}

function ProofActionCard({ node }) {
  return (
    <div
      className="absolute rounded-xl border border-purple-300 bg-white shadow-sm"
      style={{ left: node.x, top: node.y, width: node.width, height: node.height }}
      data-workflow-proof-node={node.id}
      data-workflow-proof-node-type={node.type}
    >
      <div className="border-b border-slate-100 px-4 py-2 text-sm font-semibold text-slate-800">{node.label}</div>
      <div className="px-4 py-2 text-xs text-slate-500">{node.sublabel}</div>
    </div>
  );
}

function ProofConditionCard({ node }) {
  return (
    <div
      className="absolute rounded-xl border border-purple-300 border-b-4 border-b-purple-500 bg-white shadow-sm"
      style={{ left: node.x, top: node.y, width: node.width, height: node.height }}
      data-workflow-proof-node={node.id}
      data-workflow-proof-node-type={node.type}
    >
      <div className="border-b border-slate-100 px-4 py-2 text-sm font-semibold text-purple-700">{node.label}</div>
      <div className="px-4 py-2 text-xs text-slate-500">{node.sublabel}</div>
    </div>
  );
}

function ProofBranchCard({ node }) {
  return (
    <div
      className={`absolute rounded-xl border bg-white shadow-sm ${
        node.isNone ? 'border-slate-200' : 'border-purple-200 border-b-4 border-b-purple-500'
      }`}
      style={{ left: node.x, top: node.y, width: node.width, height: node.height }}
      data-workflow-proof-node={node.id}
      data-workflow-proof-node-type={node.type}
    >
      <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
        <GitBranch size={14} className="text-purple-600" />
        <span className="text-sm font-semibold text-purple-700">{node.label}</span>
      </div>
      <div className="px-3 py-2 text-xs text-slate-500 min-h-[40px]">
        {node.isNone ? 'When none of the conditions are met' : 'If No conditions set'}
      </div>
    </div>
  );
}

function ProofPlusButton({ node }) {
  return (
    <div
      className="absolute flex items-center justify-center rounded-full border border-zinc-600 bg-white shadow-sm"
      style={{ left: node.x, top: node.y, width: node.width, height: node.height }}
      data-workflow-proof-node={node.id}
      data-workflow-proof-node-type={node.type}
    >
      <Plus size={14} className="text-zinc-400" />
    </div>
  );
}

function ProofEndPill({ node }) {
  return (
    <div
      className="absolute flex items-center justify-center rounded-full bg-zinc-800 text-[10px] font-semibold uppercase tracking-wide text-zinc-400"
      style={{ left: node.x, top: node.y, width: node.width, height: node.height }}
      data-workflow-proof-node={node.id}
      data-workflow-proof-node-type={node.type}
    >
      {node.label}
    </div>
  );
}

function ProofNode({ node }) {
  switch (node.type) {
    case 'trigger':
      return <ProofTriggerCard node={node} />;
    case 'addTrigger':
      return <ProofAddTriggerCard node={node} />;
    case 'action':
      return <ProofActionCard node={node} />;
    case 'condition':
      return <ProofConditionCard node={node} />;
    case 'branch':
      return <ProofBranchCard node={node} />;
    case 'plus':
      return <ProofPlusButton node={node} />;
    case 'end':
      return <ProofEndPill node={node} />;
    default:
      return null;
  }
}

const TriggerConnectorProofPage = () => {
  const [triggerCount, setTriggerCount] = useState(1);
  const [longLabels, setLongLabels] = useState(false);
  const [branchCount, setBranchCount] = useState(3);
  const [narrowWidth, setNarrowWidth] = useState(false);
  const [scenario, setScenario] = useState('triggers');
  const [conditionEnabled, setConditionEnabled] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const canvasWidth = narrowWidth ? 860 : 1200;
  const canvasHeight = 720;
  const isStableLaneScenario = scenario === 'stable-main-lane-condition-toggle'
    || scenario === 'stable-ghl-ifelse-behavior';
  const isStableDrawerScenario = scenario === 'stable-drawer-open-behavior';
  const isInteractiveScenario = scenario === 'interactive-add-action-behavior';
  const stableComparisonScenario = scenario === 'stable-ghl-ifelse-behavior'
    ? 'stable-ghl-ifelse-behavior'
    : 'stable-main-lane-condition-toggle';

  const schema = useMemo(() => buildWorkflowProofCanvasSchema({
    scenario,
    triggerCount,
    branchCount,
    canvasWidth,
    canvasHeight,
    longLabels,
    includeAddTile: true,
    includeCondition: isStableLaneScenario ? conditionEnabled : null,
    drawerOpen: isStableDrawerScenario ? drawerOpen : false,
    actionCount: isStableDrawerScenario ? 3 : null,
  }), [branchCount, canvasHeight, canvasWidth, conditionEnabled, drawerOpen, isStableDrawerScenario, isStableLaneScenario, longLabels, scenario, triggerCount]);

  const stableLaneComparison = useMemo(() => {
    if (!isStableLaneScenario) return null;
    return buildStableMainLaneProofComparison({
      triggerCount,
      branchCount,
      canvasWidth,
      canvasHeight,
      longLabels,
      includeAddTile: true,
      scenario: stableComparisonScenario,
    });
  }, [branchCount, canvasHeight, canvasWidth, isStableLaneScenario, longLabels, stableComparisonScenario, triggerCount]);

  const stableDrawerComparison = useMemo(() => {
    if (!isStableDrawerScenario) return null;
    return buildStableDrawerProofComparison({
      triggerCount,
      actionCount: 3,
      canvasWidth,
      canvasHeight,
      longLabels,
      includeAddTile: true,
    });
  }, [canvasHeight, canvasWidth, isStableDrawerScenario, longLabels, triggerCount]);

  const connectorRenderData = useMemo(
    () => buildSchemaConnectorPaths(schema),
    [schema],
  );
  const connectorPaths = connectorRenderData.paths;

  const gridStyle = buildCanvasGridStyle({ x: 0, y: 0 });

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="border-b border-zinc-800 bg-zinc-900 px-6 py-4">
        <h1 className="text-lg font-semibold">Trigger &amp; Branch Connector Proof</h1>
        <p className="mt-1 text-sm text-zinc-400">
          {isInteractiveScenario
            ? 'Interactive proof harness — click +, add actions, open editors, verify graph stability.'
            : 'Schema-driven localhost canvas. Content-relative coordinates only.'}
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {['triggers', 'condition-only', 'condition-below-action', 'full-flow', 'stable-main-lane-condition-toggle', 'stable-ghl-ifelse-behavior', 'stable-drawer-open-behavior', 'interactive-add-action-behavior'].map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setScenario(value)}
              className={`rounded-md px-3 py-1.5 text-sm ${scenario === value ? 'bg-emerald-600' : 'bg-zinc-800'}`}
              data-testid={`proof-scenario-${value}`}
            >
              {value}
            </button>
          ))}
        </div>
        {!isInteractiveScenario ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {[0, 1, 2, 3].map((count) => (
            <button
              key={count}
              type="button"
              onClick={() => setTriggerCount(count)}
              className={`rounded-md px-3 py-1.5 text-sm ${triggerCount === count ? 'bg-emerald-600' : 'bg-zinc-800'}`}
            >
              {count} trigger{count === 1 ? '' : 's'}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setBranchCount((value) => (value === 2 ? 3 : 2))}
            className="rounded-md bg-zinc-800 px-3 py-1.5 text-sm"
          >
            Toggle {branchCount === 2 ? '3' : '2'} branches
          </button>
          <button
            type="button"
            onClick={() => setLongLabels((value) => !value)}
            className={`rounded-md px-3 py-1.5 text-sm ${longLabels ? 'bg-emerald-600' : 'bg-zinc-800'}`}
          >
            Long labels
          </button>
          <button
            type="button"
            onClick={() => setNarrowWidth((value) => !value)}
            className={`rounded-md px-3 py-1.5 text-sm ${narrowWidth ? 'bg-emerald-600' : 'bg-zinc-800'}`}
          >
            Narrow width
          </button>
          {isStableLaneScenario ? (
            <button
              type="button"
              onClick={() => setConditionEnabled((value) => !value)}
              className={`rounded-md px-3 py-1.5 text-sm ${conditionEnabled ? 'bg-emerald-600' : 'bg-zinc-800'}`}
              data-testid="proof-condition-toggle"
            >
              Condition {conditionEnabled ? 'on' : 'off'}
            </button>
          ) : null}
          {isStableDrawerScenario ? (
            <button
              type="button"
              onClick={() => setDrawerOpen((value) => !value)}
              className={`rounded-md px-3 py-1.5 text-sm ${drawerOpen ? 'bg-emerald-600' : 'bg-zinc-800'}`}
              data-testid="proof-drawer-toggle"
            >
              Drawer {drawerOpen ? 'open' : 'closed'}
            </button>
          ) : null}
        </div>
        ) : null}
      </div>

      {isInteractiveScenario ? (
        <div className="px-6 py-10">
          <InteractiveAddActionProofHarness />
        </div>
      ) : (
      <div className="mx-auto max-w-[1440px] px-6 py-10">
        <div
          className="relative mx-auto overflow-hidden rounded-xl border border-zinc-300"
          style={{
            ...gridStyle,
            width: canvasWidth,
            height: canvasHeight,
          }}
          data-testid="trigger-connector-proof-canvas-shell"
        >
          <div
            className="h-full w-full overflow-x-auto overflow-y-hidden"
            data-testid="trigger-connector-proof-scroll"
          >
            <div
              className="relative"
              style={{
                width: schema.content.width,
                height: schema.content.height,
                minWidth: schema.content.width,
              }}
              data-testid="trigger-connector-proof-content"
            >
              <svg
                className="pointer-events-none absolute inset-0"
                width={schema.content.width}
                height={schema.content.height}
                aria-hidden="true"
                data-workflow-proof-connector-svg
                data-testid="workflow-proof-connector-svg"
              >
                {connectorPaths.map((path, index) => (
                  <path
                    key={`connector-${index}`}
                    d={path}
                    fill="none"
                    stroke={STROKE}
                    strokeWidth={WORKFLOW_CONNECTOR_LINE.width}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    shapeRendering="geometricPrecision"
                    data-workflow-proof-connector-path
                  />
                ))}
              </svg>

              {schema.nodes.map((node) => (
                <ProofNode key={node.id} node={node} />
              ))}
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <span
            className={`rounded-full px-3 py-1 text-xs font-semibold ${
              (isStableDrawerScenario
                ? !stableDrawerComparison?.didMainLaneMove && !stableDrawerComparison?.didConnectorPathsChange
                : isStableLaneScenario
                  ? !stableLaneComparison?.didMainLaneMove
                  : schema.debug.aligned)
                ? 'bg-emerald-600/20 text-emerald-300'
                : 'bg-rose-600/20 text-rose-300'
            }`}
            data-testid="workflow-proof-aligned-badge"
          >
            {isStableDrawerScenario
              ? (stableDrawerComparison?.didMainLaneMove || stableDrawerComparison?.didConnectorPathsChange
                ? 'GRAPH DRIFTED'
                : 'GRAPH STABLE')
              : isStableLaneScenario
                ? (stableLaneComparison?.didMainLaneMove ? 'MAIN LANE MOVED' : 'MAIN LANE STABLE')
                : (schema.debug.aligned ? 'ALIGNED' : 'MISALIGNED')}
          </span>
          {schema.content.usesHorizontalScroll ? (
            <span className="text-xs text-zinc-400">Horizontal scroll active inside canvas</span>
          ) : null}
        </div>

        <pre
          className="mt-4 overflow-auto rounded-lg bg-zinc-900 p-4 text-xs text-zinc-300"
          data-testid="workflow-proof-debug"
        >
          {JSON.stringify(
            isStableDrawerScenario && stableDrawerComparison
              ? {
                scenario,
                drawerOpen,
                graphRootWidth: schema.debug.graphRootWidth,
                visualViewportWidth: schema.debug.visualViewportWidth,
                mainLaneXBefore: stableDrawerComparison.mainLaneXBefore,
                mainLaneXAfter: stableDrawerComparison.mainLaneXAfter,
                triggerMergeCenterBefore: stableDrawerComparison.triggerMergeCenterBefore,
                triggerMergeCenterAfter: stableDrawerComparison.triggerMergeCenterAfter,
                actionCenters: stableDrawerComparison.open.actionCenters,
                plusCenters: stableDrawerComparison.open.plusCenters,
                connectorPathsHashBefore: stableDrawerComparison.connectorPathsHashBefore,
                connectorPathsHashAfter: stableDrawerComparison.connectorPathsHashAfter,
                didMainLaneMove: stableDrawerComparison.didMainLaneMove,
                didConnectorPathsChange: stableDrawerComparison.didConnectorPathsChange,
                current: schema.debug,
              }
              : isStableLaneScenario && stableLaneComparison
                ? {
                  scenario,
                  conditionEnabled,
                  mainLaneXBefore: stableLaneComparison.mainLaneXBefore,
                  mainLaneXAfter: stableLaneComparison.mainLaneXAfter,
                  triggerMergeCenterBefore: stableLaneComparison.triggerMergeCenterBefore,
                  triggerMergeCenterAfter: stableLaneComparison.triggerMergeCenterAfter,
                  actionCenterBefore: stableLaneComparison.actionCenterBefore,
                  actionCenterAfter: stableLaneComparison.actionCenterAfter,
                  conditionCenter: stableLaneComparison.conditionCenter,
                  branchRowWidth: stableLaneComparison.branchRowWidth,
                  didMainLaneMove: stableLaneComparison.didMainLaneMove,
                  current: schema.debug,
                }
                : schema.debug,
            null,
            2,
          )}
        </pre>
      </div>
      )}
    </div>
  );
};

export default TriggerConnectorProofPage;
